import React, { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import { Reveal } from './ui/Reveal';
import VideoPlayer from './ui/VideoPlayer';
import { videos, parseVideoUrl, type VideoItem } from '../lib/videos';
import { useVideoThumbnail } from '../lib/useVideoThumbnail';

/** Small thumbnail used in the playlist rail, with the same fallback chain as the player. */
const PlaylistThumb: React.FC<{ item: VideoItem }> = ({ item }) => {
    const thumb = useVideoThumbnail(item.url, item.poster);

    if (!thumb.src) {
        return <div className="w-full h-full bg-gradient-to-br from-brand-blue/25 via-navy-800 to-brand-cyan/15" />;
    }
    return (
        <img
            src={thumb.src}
            alt=""
            loading="lazy"
            onError={thumb.onError}
            onLoad={thumb.onLoad}
            className="w-full h-full object-cover"
        />
    );
};

const Videos: React.FC = () => {
    const playable = videos.filter((v) => parseVideoUrl(v.url));
    const [activeIndex, setActiveIndex] = useState(0);

    // Nothing configured yet: stay invisible in production, and show the author
    // a reminder of where to paste links while running locally.
    if (playable.length === 0) {
        if (process.env.NODE_ENV !== 'development') return null;
        return (
            <section id="videos" className="relative py-24 md:py-28">
                <div className="container-x">
                    <div className="max-w-2xl mx-auto text-center rounded-2xl card-surface border-dashed p-10">
                        <PlayCircle className="w-10 h-10 text-brand-cyan mx-auto" />
                        <h2 className="mt-4 font-display text-2xl font-bold text-white">Video section (dev-only preview)</h2>
                        <p className="mt-3 text-text-muted leading-relaxed">
                            Add YouTube or Loom links to the <code className="text-brand-cyan">videos</code> array in{' '}
                            <code className="text-brand-cyan">src/lib/videos.ts</code> and this section appears here.
                            While the list is empty it is hidden from the live site — visitors never see this box.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    const active = playable[Math.min(activeIndex, playable.length - 1)];
    const hasRail = playable.length > 1;

    return (
        <section id="videos" className="relative py-24 md:py-32">
            <div className="container-x">
                <SectionHeading
                    className="mb-14"
                    eyebrow="Watch it work"
                    title={<>Automation, <span className="text-gradient">in motion</span></>}
                    subtitle="Short walkthroughs of the systems we build — recorded from the real thing, not a mockup."
                />

                <Reveal className={hasRail ? 'grid lg:grid-cols-[1.7fr_1fr] gap-6' : 'max-w-4xl mx-auto'}>
                    <div>
                        <VideoPlayer url={active.url} title={active.title} poster={active.poster} />
                        <h3 className="mt-5 font-display text-xl md:text-2xl font-bold text-white">{active.title}</h3>
                        {active.description && (
                            <p className="mt-2 text-text-muted leading-relaxed">{active.description}</p>
                        )}
                    </div>

                    {hasRail && (
                        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:max-h-[26rem] pb-2 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0 lg:pr-1">
                            {playable.map((item, i) => {
                                const isActive = item === active;
                                return (
                                    <button
                                        key={item.url + i}
                                        type="button"
                                        onClick={() => setActiveIndex(i)}
                                        aria-current={isActive}
                                        className={`group flex shrink-0 lg:shrink w-64 lg:w-full items-center gap-3 rounded-xl border p-2 text-left transition-colors ${
                                            isActive
                                                ? 'bg-white/[0.06] border-brand-cyan/40'
                                                : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.16]'
                                        }`}
                                    >
                                        <span className="relative w-24 shrink-0 aspect-video overflow-hidden rounded-lg bg-navy-800">
                                            <PlaylistThumb item={item} />
                                            <span className="absolute inset-0 grid place-items-center bg-navy-950/40">
                                                <PlayCircle className="w-5 h-5 text-white/90" />
                                            </span>
                                        </span>
                                        <span className="min-w-0">
                                            <span
                                                className={`block text-sm font-semibold leading-snug line-clamp-2 transition-colors ${
                                                    isActive ? 'text-white' : 'text-text-main group-hover:text-white'
                                                }`}
                                            >
                                                {item.title}
                                            </span>
                                            <span className="block mt-1 text-[11px] uppercase tracking-wider text-text-faint">
                                                {parseVideoUrl(item.url)?.label}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Reveal>
            </div>
        </section>
    );
};

export default Videos;
